<template>
  <div class="registry-calendar">
    <!-- Month navigation -->
    <div
      class="is-flex is-align-items-center is-justify-content-space-between mb-4"
    >
      <b-button
        icon-left="chevron-left"
        size="is-small"
        :loading="isMonthLoading"
        :disabled="isMonthLoading"
        @click="prevMonth"
        aria-label="Luna anterioara"
      />
      <span class="title is-5 mb-0">{{ monthTitle }}</span>
      <b-button
        icon-right="chevron-right"
        size="is-small"
        :loading="isMonthLoading"
        :disabled="isMonthLoading"
        @click="nextMonth"
        aria-label="Luna urmatoare"
      />
    </div>

    <!-- Calendar grid -->
    <b-loading :is-full-page="false" :active="isMonthLoading" />
    <div v-if="!isMonthLoading" class="calendar-grid">
      <!-- Day-of-week headers -->
      <div
        v-for="name in dayNames"
        :key="name"
        class="calendar-day-header has-text-centered has-text-weight-bold is-size-7"
      >
        {{ name }}
      </div>

      <!-- Leading empty cells -->
      <div
        v-for="n in leadingEmptyDays"
        :key="'empty-start-' + n"
        class="calendar-cell is-empty"
      ></div>

      <!-- Day cells -->
      <div
        v-for="day in daysInMonth"
        :key="'day-' + day"
        class="calendar-cell"
        :class="{
          'is-today': isTodayDay(day),
          'is-selected': selectedDay === day,
        }"
        @click="selectDay(day)"
      >
        <div
          class="day-number"
          :class="{
            'has-text-primary has-text-weight-bold': isTodayDay(day),
          }"
        >
          {{ day }}
        </div>
        <div class="entry-dots">
          <span
            v-for="item in getNonZeroCounts(day)"
            :key="item.status"
            class="entry-badge"
            :class="getBadgeClass(item.status)"
            :title="getBadgeTitle(item.status)"
          >{{ item.count }}</span>
        </div>
      </div>

      <!-- Trailing empty cells -->
      <div
        v-for="n in trailingEmptyDays"
        :key="'empty-end-' + n"
        class="calendar-cell is-empty"
      ></div>
    </div>

    <!-- Legend -->
    <div v-if="!isMonthLoading" class="is-flex is-flex-wrap-wrap mt-3 mb-2" style="gap: 0.75rem">
      <span class="is-flex is-align-items-center" style="gap: 0.3rem">
        <span class="entry-badge-dot is-green"></span>
        <small>Rezolvat</small>
      </span>
      <span class="is-flex is-align-items-center" style="gap: 0.3rem">
        <span class="entry-badge-dot is-red"></span>
        <small>Termen depasit</small>
      </span>
      <span class="is-flex is-align-items-center" style="gap: 0.3rem">
        <span class="entry-badge-dot is-yellow"></span>
        <small>Termen apropiat (&le;3 zile)</small>
      </span>
      <span class="is-flex is-align-items-center" style="gap: 0.3rem">
        <span class="entry-badge-dot is-blue"></span>
        <small>In lucru</small>
      </span>
    </div>

    <!-- Selected day loading -->
    <div v-if="isDayLoading" class="box mt-4" style="min-height: 80px; position: relative;">
      <b-loading :is-full-page="false" :active="isDayLoading" />
    </div>

    <!-- Selected day detail panel -->
    <div v-if="selectedDay !== null && !isMonthLoading && !isDayLoading" class="box mt-4">
      <div
        class="is-flex is-align-items-center is-justify-content-space-between mb-3"
      >
        <h4 class="title is-6 mb-0">
          Inregistrari cu termen pe {{ selectedDateLabel }}
        </h4>
        <b-button
          size="is-small"
          icon-left="close"
          @click="selectedDay = null"
          aria-label="Inchide"
        />
      </div>
      <div
        v-if="selectedEntries.length === 0"
        class="has-text-grey has-text-centered py-4"
      >
        <b-icon icon="calendar-blank" size="is-medium" />
        <p class="mt-2">Nici o inregistrare cu termen in aceasta zi.</p>
      </div>
      <b-table
        v-else
        :data="selectedEntries"
        :striped="true"
        :hoverable="true"
        :mobile-cards="true"
        size="is-small"
      >
        <b-table-column
          field="internalRegistrationNumber"
          label="Nr. Inreg."
          centered
          v-slot="props"
        >
          {{ props.row.internalRegistrationNumber }}
        </b-table-column>
        <b-table-column field="description" label="Continut" v-slot="props">
          {{ props.row.description }}
        </b-table-column>
        <b-table-column field="issuer" label="Emitent" v-slot="props">
          {{ props.row.issuer }}
        </b-table-column>
        <b-table-column field="department" label="Departament" v-slot="props">
          {{ props.row.department }}
        </b-table-column>
        <b-table-column
          field="deadline"
          label="Data limita"
          centered
          v-slot="props"
        >
          {{ props.row.deadline | formatDate }}
        </b-table-column>
        <b-table-column field="status" label="Status" centered v-slot="props">
          <span :class="getStatusTagClass(props.row)">
            <b-icon :icon="getStatusIcon(props.row)" size="is-small" />
            {{ props.row.status }}
          </span>
        </b-table-column>
        <b-table-column label="Actiuni" centered v-slot="props">
          <router-link
            :to="{
              name: 'registryEntry.edit',
              params: {
                registryId: props.row.registryId,
                id: props.row.id,
              },
            }"
            class="button is-small is-primary"
            title="Editare"
            aria-label="Editare"
          >
            <b-icon icon="file-edit" size="is-small" />
          </router-link>
        </b-table-column>
      </b-table>
    </div>
  </div>
</template>

<script>
import moment from "moment";

const ROMANIAN_MONTHS = [
  "Ianuarie",
  "Februarie",
  "Martie",
  "Aprilie",
  "Mai",
  "Iunie",
  "Iulie",
  "August",
  "Septembrie",
  "Octombrie",
  "Noiembrie",
  "Decembrie",
];

const APPROACHING_DEADLINE_DAYS = 3;

export default {
  name: "RegistryEntriesCalendar",
  props: {
    entries: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentYear: moment().year(),
      currentMonth: moment().month(), // 0-indexed
      selectedDay: null,
      isMonthLoading: false,
      isDayLoading: false,
      dayNames: ["Lun", "Mar", "Mie", "Joi", "Vin", "Sâm", "Dum"],
    };
  },
  computed: {
    monthTitle() {
      return `${ROMANIAN_MONTHS[this.currentMonth]} ${this.currentYear}`;
    },
    daysInMonth() {
      return moment({
        year: this.currentYear,
        month: this.currentMonth,
      }).daysInMonth();
    },
    leadingEmptyDays() {
      // moment day(): 0=Sunday ... 6=Saturday; calendar starts on Monday
      const firstDay = moment({
        year: this.currentYear,
        month: this.currentMonth,
        day: 1,
      }).day();
      return firstDay === 0 ? 6 : firstDay - 1;
    },
    trailingEmptyDays() {
      const totalCells = this.leadingEmptyDays + this.daysInMonth;
      const remainder = totalCells % 7;
      return remainder === 0 ? 0 : 7 - remainder;
    },
    selectedEntries() {
      if (this.selectedDay === null) return [];
      return this.getEntriesForDay(this.selectedDay);
    },
    selectedDateLabel() {
      if (this.selectedDay === null) return "";
      const day = String(this.selectedDay).padStart(2, "0");
      const month = ROMANIAN_MONTHS[this.currentMonth];
      return `${day} ${month} ${this.currentYear}`;
    },
  },
  methods: {
    changeMonth(fn) {
      this.isMonthLoading = true;
      this.selectedDay = null;
      this.$nextTick(() => {
        setTimeout(() => {
          fn();
          this.$nextTick(() => {
            this.isMonthLoading = false;
          });
        }, 50);
      });
    },
    prevMonth() {
      this.changeMonth(() => {
        if (this.currentMonth === 0) {
          this.currentMonth = 11;
          this.currentYear--;
        } else {
          this.currentMonth--;
        }
      });
    },
    nextMonth() {
      this.changeMonth(() => {
        if (this.currentMonth === 11) {
          this.currentMonth = 0;
          this.currentYear++;
        } else {
          this.currentMonth++;
        }
      });
    },
    isTodayDay(day) {
      const today = moment();
      return (
        today.year() === this.currentYear &&
        today.month() === this.currentMonth &&
        today.date() === day
      );
    },
    getEntriesForDay(day) {
      if (!this.entries) return [];
      return this.entries.filter((entry) => {
        if (!entry.deadline) return false;
        const d = moment(entry.deadline);
        return (
          d.isValid() &&
          d.year() === this.currentYear &&
          d.month() === this.currentMonth &&
          d.date() === day
        );
      });
    },
    getEntryStatus(entry) {
      if (entry.status === "Rezolvat") return "resolved";
      const today = moment().startOf("day");
      const deadline = moment(entry.deadline).startOf("day");
      const daysUntil = deadline.diff(today, "days");
      if (daysUntil < 0) return "overdue";
      if (daysUntil <= APPROACHING_DEADLINE_DAYS) return "approaching";
      return "normal";
    },
    getCountsByStatus(day) {
      const counts = { resolved: 0, overdue: 0, approaching: 0, normal: 0 };
      this.getEntriesForDay(day).forEach((entry) => {
        counts[this.getEntryStatus(entry)]++;
      });
      return counts;
    },
    getNonZeroCounts(day) {
      const counts = this.getCountsByStatus(day);
      return Object.entries(counts)
        .filter(([, count]) => count > 0)
        .map(([status, count]) => ({ status, count }));
    },
    getBadgeClass(status) {
      const map = {
        resolved: "is-green",
        overdue: "is-red",
        approaching: "is-yellow",
        normal: "is-blue",
      };
      return map[status] || "";
    },
    getBadgeTitle(status) {
      const map = {
        resolved: "Rezolvat",
        overdue: "Termen depasit",
        approaching: "Termen apropiat",
        normal: "In lucru",
      };
      return map[status] || "";
    },
    selectDay(day) {
      if (this.selectedDay === day) {
        this.selectedDay = null;
        return;
      }
      this.isDayLoading = true;
      this.selectedDay = day;
      this.$nextTick(() => {
        setTimeout(() => {
          this.isDayLoading = false;
        }, 150);
      });
    },
    getStatusTagClass(entry) {
      if (entry.status === "Rezolvat") return "tag is-success";
      if (entry.status === "În lucru") return "tag is-warning";
      return "tag";
    },
    getStatusIcon(entry) {
      if (entry.status === "Rezolvat") return "check-circle";
      if (entry.status === "În lucru") return "clock-outline";
      return "";
    },
  },
};
</script>

<style scoped>
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.calendar-day-header {
  padding: 0.4rem 0.2rem;
  background: #f5f5f5;
  border: 1px solid #dbdbdb;
}

.calendar-cell {
  min-height: 72px;
  border: 1px solid #dbdbdb;
  padding: 0.3rem;
  cursor: pointer;
  transition: background 0.15s ease;
  background: #ffffff;
}

.calendar-cell:hover:not(.is-empty) {
  background: #f0f9ff;
}

.calendar-cell.is-today {
  background: #e8f8f5;
  border-color: #00d1b2;
}

.calendar-cell.is-selected {
  background: #eef3fb;
  border-color: #3273dc;
  border-width: 2px;
}

.calendar-cell.is-empty {
  background: #fafafa;
  cursor: default;
}

.day-number {
  font-size: 0.82rem;
  font-weight: 500;
  margin-bottom: 0.2rem;
}

.entry-dots {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.entry-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  font-size: 0.65rem;
  font-weight: bold;
  padding: 0 4px;
  color: #fff;
}

.entry-badge.is-green {
  background: rgb(54, 162, 93);
}

.entry-badge.is-red {
  background: rgb(255, 99, 132);
}

.entry-badge.is-yellow {
  background: rgb(200, 160, 20);
}

.entry-badge.is-blue {
  background: #3273dc;
}

.entry-badge-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.entry-badge-dot.is-green {
  background: rgb(54, 162, 93);
}

.entry-badge-dot.is-red {
  background: rgb(255, 99, 132);
}

.entry-badge-dot.is-yellow {
  background: rgb(200, 160, 20);
}

.entry-badge-dot.is-blue {
  background: #3273dc;
}
</style>
